import { ComponentBase, PageBase } from "../../lib/ssi/form.types";
import { PageType } from "../../lib/types/form/page-type";
import { Attachment } from "../../lib/types/form/submission/attachment";
import { Score } from "../../lib/types/form/submission/score";
import { ComponentId, PageId } from "../../lib/types/ids";
import { PlainObject, PlainValue } from "../../lib/types/json/crypto";
import { UnexpectedValueError } from "../../lib/unexpected-value-error";
import { UnreachableVariantError } from "../../lib/unreachable-variant-error";
import * as t from "io-ts";

export type UploadedFileType = "application/pdf" | "image/jpeg" | "image/png" | "image/jpg";

export const uploadedFileTypes: UploadedFileType[] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
];

export function isUploadedFileTypeAccepted(value: string): value is UploadedFileType {
  return uploadedFileTypes.includes(value as UploadedFileType);
}

export interface UploadedFile {
  buffer: Buffer,
  type: UploadedFileType,
}

export type InputValue = string | boolean | number | UploadedFile;

export function isUploadedFile(value: InputValue): value is UploadedFile {
  return typeof value === "object";
}

type FormInput = Record<ComponentId, InputValue>;

export type AnswerChoiceValue = InputValue | Score | undefined;

export function toPlainValue(value: AnswerChoiceValue): PlainValue | undefined {
  if (typeof value === "string") {
    return UnexpectedValueError.throwIfLeft("answer.value (string)",
      PlainValue.decode(value))
  }

  if (typeof value === "undefined" || typeof value === "boolean" || typeof value === "number" || Score.is(value)) {
    return value;
  }

  if (isUploadedFile(value)) {
    return UnexpectedValueError.throwIfNot("answer.value (UploadedFile)", PlainObject,
      Attachment.encode({
        type: value.type,
        buffer: {
          data: [...value.buffer]
        },
      }));
  }

  throw new UnreachableVariantError(value);
}

export interface AnswerChoice {
  id: ComponentId;
  label: string;
  value: AnswerChoiceValue;
}

export type AnswerChoices = Record<ComponentId, AnswerChoice>;

export const toAnswerChoicesFromArray = (components: ComponentBase[], state: AnswerChoiceForFormData[]): AnswerChoices => {
  const choices: AnswerChoices = {};
  for (const item of state) {
    choices[item.id] = { id: item.id, value: item.value, label: item.label };
  }
  return choices;
};

export const toAnswerChoicesFromDict = (components: ComponentBase[], state: FormInput): AnswerChoices => {
  const choices: AnswerChoices = {};
  for (const id of Object.keys(state)) {
    const component = components.find((el) => el.id === id);
    if (!component) {
      throw new UnexpectedValueError(component);
    }

    choices[id] = { id, value: state[id], label: component.label };
  }
  return choices;
};

export interface AnswerChoiceForFormData {
  id: ComponentId;
  label: string;
  value?: InputValue | Score;
}

export function toFormData(type: FormType, data: PageBase, state: FormInput | AnswerChoiceForFormData[], customAnswer?: AnswerChoice): FormData {
  const choices: AnswerChoices = customAnswer
    ? { [customAnswer.id]: customAnswer }
    : Array.isArray(state)
      ? toAnswerChoicesFromArray(data.components, state)
      : toAnswerChoicesFromDict(data.components, state);
  const selectedData = {
    type: type,
    page: { type: data.type, id: data.id },
    choices: choices,
  };
  return selectedData;
}

export const FormType = t.keyof({
  "multiple_choice": null,
  "single_choice": null,
  "input": null,
  "opt-in": null,
});
export type FormType = t.TypeOf<typeof FormType>;

export type FormData = {
  type: FormType;
  page: {
    type: PageType;
    id: PageId;
  };
  choices: AnswerChoices;
};
